import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { Modal } from '@/components/shared/Modal';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { useCloneProcedure } from '../hooks/useProcedureMutations';
import {
  EquipmentModelCombobox,
  type EquipmentModelOption,
} from '@/components/shared/EquipmentModelCombobox';
import { ROUTES } from '@/routes/routes';
import type { ProcedureType } from '@/services/procedure-template.service';

export interface CloneSource {
  id: string;
  title: string;
  procedureType: ProcedureType;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

interface CloneDialogProps {
  source: CloneSource;
  onClose: () => void;
}

export function CloneDialog({ source, onClose }: CloneDialogProps) {
  const [title, setTitle] = useState(`Copy of ${source.title}`);
  const [type, setType] = useState<ProcedureType>(source.procedureType);
  const [model, setModel] = useState<EquipmentModelOption | null>(null);
  const cloneMutation = useCloneProcedure();
  const navigate = useNavigate();

  async function handleClone() {
    if (title.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }
    if (!model) {
      toast.error('Equipment model is required');
      return;
    }
    try {
      const result = await cloneMutation.mutateAsync({
        id: source.id,
        dto: { title: title.trim(), equipmentModelId: model.id, procedureType: type },
      });
      toast.success('Procedure cloned successfully');
      onClose();
      navigate(ROUTES.PROCEDURE_EDIT.replace(':id', result.id));
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to clone'));
    }
  }

  return (
    <Modal open={true} onClose={onClose} title="Clone Procedure">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Source: <span className="font-medium text-foreground">{source.title}</span>
        </p>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            New Title <span className="text-destructive">*</span>
          </label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Procedure Type <span className="text-destructive">*</span>
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ProcedureType)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="INSPECTION">Inspection</option>
            <option value="SERVICE">Service</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Equipment Model <span className="text-destructive">*</span>
          </label>
          <EquipmentModelCombobox value={model} onChange={setModel} />
          {!model && (
            <p className="text-xs text-muted-foreground">
              Search and select the target equipment model
            </p>
          )}
        </div>
        <p className="rounded-lg bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          All sections, steps, and reference materials will be copied into a new Draft procedure.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={cloneMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleClone()} disabled={cloneMutation.isPending}>
            {cloneMutation.isPending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
            )}
            Clone Procedure
          </Button>
        </div>
      </div>
    </Modal>
  );
}
